import { db } from "./db";
import {
  obtenerProductoOffline,
  obtenerPrecioOffline,
  obtenerStockTotalOffline,
} from "./offlineCatalogoService";

function buildFolioLocal() {
  const now = new Date();

  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const mi = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");

  return `OFF-${yyyy}${mm}${dd}-${hh}${mi}${ss}`;
}

function getId(value) {
  if (value && typeof value === "object") {
    return value.id ?? value.codigo ?? value.almacen ?? "";
  }

  return value ?? "";
}

export async function crearCotizacionOffline(data, tipoDocumento) {
  const fechaCreacion = new Date().toISOString();
  const folioLocal = buildFolioLocal();

  const cotizacion = {
    folioLocal,
    tipoDocumento,
    estadoSync: "PENDIENTE",
    fechaCreacion,
    fechaActualizacion: fechaCreacion,

    codigoCliente: data.cliente?.codigo || "",
    cliente: data.cliente || null,

    documento: data.documento,
    contacto: data.contacto || null,
    idContacto: getId(data.contacto),

    estado: data.estado || null,
    idEstado: getId(data.estado),

    validez: data.validez || data.validezs?.[0]?.id || null,
    idValidez: getId(data.validez || data.validezs?.[0]),

    proyecto: data.proyecto || null,
    idProyecto: getId(data.proyecto),

    obra: data.obra || null,
    idObra: getId(data.obra),

    almacen: data.almacen || null,
    idAlmacen:
      data.interfaz === "MAYORISTA"
        ? data.almacen?.almacen
        : data.cliente?.listaPrecio?.almacen,

    listaPrecio: data.cliente?.listaPrecio || null,
    idListaPrecio: data.cliente?.listaPrecio?.codigo || "",

    condicionPago: data.cliente?.condicionPago || null,
    condicion: "3",

    fechaDecision: data.fechaDecision || data.fecha || null,
    fechaEntrega: data.fechaEntrega || data.fecha || null,

    interfaz: data.interfaz || "RETAIL",
    clienteBoleta: data.clienteBoleta || null,

    payloadOriginal: data,
  };

  const idLocal = await db.offline_cotizaciones.add(cotizacion);

  await db.offline_outbox.add({
    tipo: "CREAR_COTIZACION",
    estado: "PENDIENTE",
    fechaCreacion,
    idCotizacionLocal: idLocal,
    folioLocal,
  });

  return {
    idLocal,
    folioLocal,
  };
}

export async function obtenerCotizacionOffline(idLocal) {
  return db.offline_cotizaciones.get(Number(idLocal));
}

export async function listarCotizacionesPendientesOffline() {
  return db.offline_cotizaciones
    .where("estadoSync")
    .equals("PENDIENTE")
    .toArray();
}

function toNumber(value) {
  const number = Number(value);
  return Number.isNaN(number) ? 0 : number;
}

export async function listarDetallesCotizacionOffline(idCotizacionLocal) {
  const detalles = await db.offline_cotizacion_detalles
    .where("idCotizacionLocal")
    .equals(Number(idCotizacionLocal))
    .toArray();

  return detalles.map((detalle) => ({
    ...detalle,
    id: detalle.idLocal,
  }));
}

export async function calcularResumenCotizacionOffline(idCotizacionLocal) {
  const detalles = await listarDetallesCotizacionOffline(idCotizacionLocal);

  const subtotal = detalles.reduce((total, item) => {
    return total + toNumber(item.cantidad) * toNumber(item.precio_lista);
  }, 0);

  const descuento = detalles.reduce((total, item) => {
    const cantidad = toNumber(item.cantidad);
    const precio = toNumber(item.precio);
    const descuentoPorcentaje = toNumber(item.descuento);

    const brutoLinea = cantidad * precio;
    const descuentoLinea = brutoLinea * (descuentoPorcentaje / 100);

    return total + descuentoLinea;
  }, 0);

  const neto = detalles.reduce((total, item) => {
    const cantidad = toNumber(item.cantidad);
    const precio = toNumber(item.precio);
    const descuentoPorcentaje = toNumber(item.descuento);

    const brutoLinea = cantidad * precio;
    const descuentoLinea = brutoLinea * (descuentoPorcentaje / 100);

    return total + brutoLinea - descuentoLinea;
  }, 0);

  const iva = Math.round(neto * 0.19);
  const total = Math.round(neto + iva);

  return {
    subtotal: Math.round(subtotal),
    descuento: Math.round(descuento),
    neto: Math.round(neto),
    iva,
    total,
  };
}

export async function agregarProductoCotizacionOffline(
  idCotizacionLocal,
  codigoProducto,
) {
  const cotizacion = await obtenerCotizacionOffline(idCotizacionLocal);

  if (!cotizacion) {
    throw new Error("Cotización local no encontrada");
  }

  const producto = await obtenerProductoOffline(codigoProducto);

  if (!producto) {
    throw new Error("Producto no encontrado");
  }

  const yaExiste = await db.offline_cotizacion_detalles
    .where("idCotizacionLocal")
    .equals(Number(idCotizacionLocal))
    .and((item) => String(item.codigoProducto) === String(codigoProducto))
    .first();

  if (yaExiste) {
    throw new Error("El producto ya fue agregado");
  }

  const listaPrecio = cotizacion.idListaPrecio;
  const almacen = cotizacion.idAlmacen;

  const precioData = await obtenerPrecioOffline(listaPrecio, codigoProducto);
  const stock = await obtenerStockTotalOffline(almacen, codigoProducto);

  const precio = toNumber(precioData?.precio);
  const fechaCreacion = new Date().toISOString();

  const detalle = {
    idCotizacionLocal: Number(idCotizacionLocal),
    codigoProducto: String(codigoProducto),

    referencia: producto.codigo,
    nombre: producto.descripcion,
    descripcion: producto.descripcion,

    cantidad: 1,
    precio,
    precio_lista: precio,
    descuento: 0,
    total: precio,

    stock,
    unidad: producto.unidad || "",
    um: producto.unidad || "",
    peso: producto.peso || "",

    centro: almacen,
    estadoSync: "PENDIENTE",
    fechaCreacion,
    fechaActualizacion: fechaCreacion,
  };

  const idLocal = await db.offline_cotizacion_detalles.add(detalle);

  await db.offline_cotizacion_detalles.update(idLocal, {
    id: idLocal,
    idLocal,
  });

  await db.offline_cotizaciones.update(Number(idCotizacionLocal), {
    fechaActualizacion: fechaCreacion,
    estadoSync: "PENDIENTE",
  });

  return {
    ...detalle,
    id: idLocal,
    idLocal,
  };
}

function recalcularTotalLinea(linea) {
  const cantidad = Number(linea.cantidad || 0);
  const precio = Number(linea.precio || 0);
  const descuento = Number(linea.descuento || 0);

  const subtotal = cantidad * precio;
  return Math.round(subtotal - subtotal * (descuento / 100));
}

export async function actualizarDetalleCotizacionOffline(
  idDetalleLocal,
  cambios,
) {
  const detalle = await db.offline_cotizacion_detalles.get(
    Number(idDetalleLocal),
  );

  if (!detalle) {
    throw new Error("Detalle local no encontrado");
  }

  const actualizado = {
    ...detalle,
    ...cambios,
    fechaActualizacion: new Date().toISOString(),
  };

  actualizado.total = recalcularTotalLinea(actualizado);

  await db.offline_cotizacion_detalles.update(
    Number(idDetalleLocal),
    actualizado,
  );

  await db.offline_cotizaciones.update(Number(detalle.idCotizacionLocal), {
    estadoSync: "PENDIENTE",
    fechaActualizacion: new Date().toISOString(),
  });

  return actualizado;
}

export async function eliminarDetalleCotizacionOffline(idDetalleLocal) {
  const detalle = await db.offline_cotizacion_detalles.get(
    Number(idDetalleLocal),
  );

  if (!detalle) {
    throw new Error("Detalle local no encontrado");
  }

  await db.offline_cotizacion_detalles.delete(Number(idDetalleLocal));

  await db.offline_cotizaciones.update(Number(detalle.idCotizacionLocal), {
    estadoSync: "PENDIENTE",
    fechaActualizacion: new Date().toISOString(),
  });
}

export async function actualizarCotizacionOffline(idCotizacionLocal, cambios) {
  const id = Number(idCotizacionLocal);

  if (!id) {
    throw new Error("ID de cotización local inválido");
  }

  await db.offline_cotizaciones.update(id, {
    ...cambios,
    estadoSync: "PENDIENTE",
    fechaActualizacion: new Date().toISOString(),
  });
}

export async function marcarTodosCajaCompletaOffline(
  idCotizacionLocal,
  checked,
) {
  const detalles = await listarDetallesCotizacionOffline(idCotizacionLocal);

  await Promise.all(
    detalles.map((detalle) =>
      actualizarDetalleCotizacionOffline(detalle.id, {
        cajaCompleta: checked,
      }),
    ),
  );
}

export async function confirmarCotizacionOffline(idCotizacionLocal) {
  const detalles = await listarDetallesCotizacionOffline(idCotizacionLocal);

  if (detalles.length === 0) {
    throw new Error("Debe agregar al menos un producto");
  }

  const cantidadesInvalidas = detalles.some(
    (item) => Number(item.cantidad || 0) <= 0,
  );

  if (cantidadesInvalidas) {
    throw new Error("La cantidad de los productos debe ser mayor a 0");
  }

  const total = detalles.reduce(
    (sum, item) => sum + Number(item.total || 0),
    0,
  );

  if (total <= 0) {
    throw new Error("El total no puede ser 0");
  }

  await actualizarCotizacionOffline(idCotizacionLocal, {
    estadoLocal: "CONFIRMADA",
  });

  return true;
}

export async function guardarCotizacionOffline(idCotizacionLocal) {
  await actualizarCotizacionOffline(idCotizacionLocal, {
    estadoLocal: "GUARDADA",
  });

  return true;
}

export async function finalizarCotizacionOffline(idCotizacionLocal) {
  const detalles = await listarDetallesCotizacionOffline(idCotizacionLocal);

  if (detalles.length === 0) {
    throw new Error("Debe agregar al menos un producto");
  }

  const cantidadesInvalidas = detalles.some(
    (item) => Number(item.cantidad || 0) <= 0,
  );

  if (cantidadesInvalidas) {
    throw new Error("La cantidad de los productos debe ser mayor a 0");
  }

  const total = detalles.reduce(
    (sum, item) => sum + Number(item.total || 0),
    0,
  );

  if (total <= 0) {
    throw new Error("El total no puede ser 0");
  }

  await actualizarCotizacionOffline(idCotizacionLocal, {
    estadoLocal: "FINALIZADA",
  });

  return true;
}

export async function descartarCotizacionOffline(idCotizacionLocal) {
  const id = Number(idCotizacionLocal);

  await db.offline_cotizacion_detalles
    .where("idCotizacionLocal")
    .equals(id)
    .delete();

  await db.offline_cotizaciones.update(id, {
    estadoSync: "DESCARTADA",
    estadoLocal: "DESCARTADA",
    fechaActualizacion: new Date().toISOString(),
  });

  const outboxItems = await db.offline_outbox.toArray();

  await Promise.all(
    outboxItems
      .filter((item) => Number(item.idCotizacionLocal) === id)
      .map((item) =>
        db.offline_outbox.update(item.idLocal, {
          estado: "DESCARTADA",
        }),
      ),
  );
}
